/**
 * Core AI planner session state — shared by PlannerProvider and isolated mounts.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ChatSession, Part } from "firebase/ai";
import { getSkillIndex } from "@amazing-wedding/planner-skills";
import type {
  AccommodationRoom,
  AccommodationVenue,
  BudgetItem,
  DayPlanEvent,
  Guest,
  GuestGroup,
  RoomElement,
  SeatingPlan,
  Table,
  Task,
  Wedding,
} from "@amazing-wedding/shared-types";
import { chatQuotaFor } from "@amazing-wedding/shared-types";
import { AiNotConfiguredError, isConfigError } from "@amazing-wedding/ai-core";
import { useIsDemoUser } from "@/features/demo/useIsDemoUser";
import { useWedding } from "@/hooks/useWedding";
import { useAuth } from "@/hooks/useAuth";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useGuests } from "@/hooks/useGuests";
import { ChatQuotaExceededError, reserveChatMessage } from "@/features/billing/chat-quota";
import { useTasks } from "@/hooks/useTasks";
import { useBudget } from "@/hooks/useBudget";
import { useTables } from "@/hooks/useTables";
import { useSeatingPlans, useActiveSeatingPlan } from "@/hooks/useSeatingPlans";
import { useGroups } from "@/hooks/useGroups";
import { useVenues, useRooms } from "@/hooks/useAccommodation";
import { useRoomElements } from "@/hooks/useRoomElements";
import { useDayPlan } from "@/hooks/useDayPlan";
import i18n, { resolveAppLanguage, type SupportedLocale } from "@/lib/i18n";
import { toMillis } from "@/lib/timestamps";
import {
  buildSystemInstruction,
  formatPlannerError,
  getPlannerModel,
} from "@amazing-wedding/ai-planner";
import { runAgentTurnStream } from "./agentLoopStream";
import { loadPlannerMemory } from "./memory/operations";
import { webPlannerOperations } from "./planner-operations.web";
import type { SeatingSetupAnswers } from "@amazing-wedding/shared-types";
import type { SeatingSetupQuestionnairePayload } from "@amazing-wedding/ai-planner";
import {
  PLANNER_GREETING_ID,
  type ChatAttachment,
  type PendingConfirmation,
  type PendingSeatingSetupQuestionnaire,
  type PlannerMessage,
  type PlannerStatus,
  type ToolContext,
  type WeddingContextSummary,
} from "./types";

const SUPPORTED_ATTACHMENT_MIME = new Set([
  "image/jpeg",
  "image/png",
  "image/heic",
  "image/webp",
  "application/pdf",
]);
const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      const idx = result.indexOf(",");
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("File read failed"));
    reader.readAsDataURL(file);
  });
}

function isImageMime(mimeType: string): boolean {
  return mimeType.startsWith("image/");
}

interface PlannerData {
  wedding: Wedding | null;
  guests: Guest[];
  tasks: Task[];
  budgetItems: BudgetItem[];
  tables: Table[];
  groups: GuestGroup[];
  venues: AccommodationVenue[];
  rooms: AccommodationRoom[];
  roomElements: RoomElement[];
  dayPlanEvents: DayPlanEvent[];
  activePlanId: string | null;
  activePlan: SeatingPlan | null;
}

const GREETING_ID = PLANNER_GREETING_ID;

function buildGreeting(language: SupportedLocale): PlannerMessage {
  return {
    id: GREETING_ID,
    role: "assistant",
    content: i18n.t("aiChat.greeting", { lng: language }),
    createdAt: new Date().toISOString(),
  };
}

function plannerUiLanguage(wedding: Wedding): SupportedLocale {
  return resolveAppLanguage(wedding.settings.language);
}

function buildSummary(
  w: Wedding,
  guests: Guest[],
  tasks: Task[],
  budget: BudgetItem[],
  activePlanName: string | null,
  tableCount: number,
): WeddingContextSummary {
  const ms = toMillis(w.weddingDate);
  return {
    coupleName1: w.coupleName1,
    coupleName2: w.coupleName2,
    weddingDate: ms ? new Date(ms).toISOString().slice(0, 10) : "to be set",
    daysUntil: ms ? Math.ceil((ms - Date.now()) / 86_400_000) : null,
    venue: w.venue ?? null,
    currency: w.settings.currency,
    language: plannerUiLanguage(w),
    guestTotal: guests.length,
    guestAccepted: guests.filter((g) => g.rsvpStatus === "accepted").length,
    guestDeclined: guests.filter((g) => g.rsvpStatus === "declined").length,
    guestPending: guests.filter((g) => g.rsvpStatus === "pending").length,
    taskTotal: tasks.length,
    taskDone: tasks.filter((t) => t.status === "done").length,
    budgetTotal: w.settings.budget,
    budgetSpent: budget.reduce((s, b) => s + b.actualCost, 0),
    activeSeatingPlanName: activePlanName,
    tableCount,
  };
}

export interface UseAIPlannerResult {
  messages: PlannerMessage[];
  status: PlannerStatus;
  progressLabel: string | null;
  pendingConfirmation: PendingConfirmation | null;
  pendingQuestionnaire: PendingSeatingSetupQuestionnaire | null;
  notConfigured: boolean;
  error: string | null;
  ready: boolean;
  send: (text: string, files?: File[]) => void;
  confirm: (approved: boolean) => void;
  submitQuestionnaire: (answers: SeatingSetupAnswers) => void;
  cancelQuestionnaire: () => void;
  reset: () => void;
}

/** Owns planner chat session state (messages, agent loop, confirmations). */
export function usePlannerSession(): UseAIPlannerResult {
  const { wedding } = useWedding();
  const { firebaseUser } = useAuth();
  const { effectivePlan } = useCurrentUser();
  const isDemoUser = useIsDemoUser();
  const { guests } = useGuests();
  const { tasks } = useTasks();
  const { items: budgetItems } = useBudget(wedding?.id ?? null);
  const { plans, loading: plansLoading } = useSeatingPlans();
  const { activePlanId, activePlan } = useActiveSeatingPlan(plans, plansLoading);
  const { tables } = useTables(activePlanId);
  const { groups } = useGroups();
  const { venues } = useVenues(wedding?.id ?? null);
  const { rooms } = useRooms(wedding?.id ?? null);
  const { elements: roomElements } = useRoomElements(activePlanId);
  const { events: dayPlanEvents } = useDayPlan();

  const dataRef = useRef<PlannerData>({
    wedding,
    guests,
    tasks,
    budgetItems,
    tables,
    groups,
    venues,
    rooms,
    roomElements,
    dayPlanEvents,
    activePlanId,
    activePlan,
  });
  useEffect(() => {
    dataRef.current = {
      wedding,
      guests,
      tasks,
      budgetItems,
      tables,
      groups,
      venues,
      rooms,
      roomElements,
      dayPlanEvents,
      activePlanId,
      activePlan,
    };
  }, [
    wedding,
    guests,
    tasks,
    budgetItems,
    tables,
    groups,
    venues,
    rooms,
    roomElements,
    dayPlanEvents,
    activePlanId,
    activePlan,
  ]);

  const plannerLanguage = wedding ? plannerUiLanguage(wedding) : resolveAppLanguage(undefined);
  const greeting = useMemo(() => buildGreeting(plannerLanguage), [plannerLanguage]);

  const [messages, setMessages] = useState<PlannerMessage[]>(() => [buildGreeting("pl")]);
  useEffect(() => {
    setMessages((prev) => {
      if (prev.length !== 1 || prev[0]?.id !== GREETING_ID) return prev;
      return [greeting];
    });
  }, [greeting]);

  const [status, setStatus] = useState<PlannerStatus>("idle");
  const [progressLabel, setProgressLabel] = useState<string | null>(null);
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | null>(null);
  const [pendingQuestionnaire, setPendingQuestionnaire] =
    useState<PendingSeatingSetupQuestionnaire | null>(null);
  const [notConfigured, setNotConfigured] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const skillIndex = useMemo(() => getSkillIndex(), []);

  const chatRef = useRef<ChatSession | null>(null);
  const busyRef = useRef(false);
  const confirmResolverRef = useRef<((approved: boolean) => void) | null>(null);
  const questionnaireResolverRef = useRef<((answers: SeatingSetupAnswers | null) => void) | null>(
    null,
  );
  const pendingLanguageResetRef = useRef(false);

  const requestConfirmation = useCallback(
    (toolName: string, description: string): Promise<boolean> =>
      new Promise<boolean>((resolve) => {
        confirmResolverRef.current = resolve;
        setPendingConfirmation({ toolName, description });
        setStatus("awaiting-confirmation");
      }),
    [],
  );

  const requestQuestionnaire = useCallback(
    (
      toolName: string,
      payload: SeatingSetupQuestionnairePayload,
    ): Promise<SeatingSetupAnswers | null> =>
      new Promise<SeatingSetupAnswers | null>((resolve) => {
        questionnaireResolverRef.current = resolve;
        setPendingQuestionnaire({ ...payload, toolName, requestId: crypto.randomUUID() });
        setStatus("awaiting-questionnaire");
      }),
    [],
  );

  const confirm = useCallback((approved: boolean) => {
    const resolve = confirmResolverRef.current;
    confirmResolverRef.current = null;
    setPendingConfirmation(null);
    if (resolve) {
      setStatus("thinking");
      resolve(approved);
    }
  }, []);

  const submitQuestionnaire = useCallback((answers: SeatingSetupAnswers) => {
    const resolve = questionnaireResolverRef.current;
    questionnaireResolverRef.current = null;
    setPendingQuestionnaire(null);
    if (resolve) {
      setStatus("thinking");
      resolve(answers);
    }
  }, []);

  const cancelQuestionnaire = useCallback(() => {
    const resolve = questionnaireResolverRef.current;
    questionnaireResolverRef.current = null;
    setPendingQuestionnaire(null);
    if (resolve) {
      setStatus("thinking");
      resolve(null);
    }
  }, []);

  const makeToolContext = useCallback(
    (weddingId: string): ToolContext => ({
      weddingId,
      wedding: () => {
        const w = dataRef.current.wedding;
        if (!w) throw new Error("No wedding is loaded.");
        return w;
      },
      guests: () => dataRef.current.guests,
      tasks: () => dataRef.current.tasks,
      budgetItems: () => dataRef.current.budgetItems,
      tables: () => dataRef.current.tables,
      groups: () => dataRef.current.groups,
      venues: () => dataRef.current.venues,
      rooms: () => dataRef.current.rooms,
      seatingPlanId: () => dataRef.current.activePlanId,
      seatingPlan: () => dataRef.current.activePlan,
      roomElements: () => dataRef.current.roomElements,
      dayPlanEvents: () => dataRef.current.dayPlanEvents,
      isDemoAccount: () => isDemoUser,
      operations: webPlannerOperations,
      translate: (key, params) => i18n.t(key, params),
    }),
    [isDemoUser],
  );

  const send = useCallback(
    async (text: string, files?: File[]) => {
      const trimmed = text.trim();
      if (!trimmed && (!files || files.length === 0)) return;
      if (busyRef.current) return;
      const data = dataRef.current;
      if (!data.wedding) {
        setError(i18n.t("aiChat.errors.noWedding"));
        return;
      }

      const validFiles: File[] = [];
      for (const f of files ?? []) {
        if (!SUPPORTED_ATTACHMENT_MIME.has(f.type)) {
          setError(
            i18n.t("aiChat.errors.unsupportedFormat", {
              format: f.type || f.name,
            }),
          );
          return;
        }
        if (f.size > MAX_ATTACHMENT_BYTES) {
          setError(
            i18n.t("aiChat.errors.fileTooLarge", {
              name: f.name,
              sizeMb: (f.size / 1024 / 1024).toFixed(1),
            }),
          );
          return;
        }
        validFiles.push(f);
      }

      busyRef.current = true;
      setError(null);
      setProgressLabel(null);

      const attachments: ChatAttachment[] = validFiles.map((f) => ({
        id: crypto.randomUUID(),
        file: f,
        previewUrl: isImageMime(f.type) ? URL.createObjectURL(f) : undefined,
      }));

      const userMessageId = crypto.randomUUID();
      setMessages((m) => [
        ...m,
        {
          id: userMessageId,
          role: "user",
          content: trimmed,
          createdAt: new Date().toISOString(),
          attachments: attachments.length > 0 ? attachments : undefined,
        },
      ]);
      setStatus("thinking");

      if (firebaseUser) {
        try {
          await reserveChatMessage(firebaseUser.uid, effectivePlan, { isDemo: isDemoUser });
        } catch (err) {
          if (err instanceof ChatQuotaExceededError) {
            const exhaustedContent = err.isDemo
              ? i18n.t("demo.aiQuotaExhausted", { cap: err.cap })
              : err.plan === "premium"
                ? i18n.t("aiChat.errors.quotaExhaustedPremium", { cap: err.cap })
                : i18n.t("aiChat.errors.quotaExhaustedFree", {
                    cap: err.cap,
                    premiumCap: chatQuotaFor("premium"),
                  });
            setMessages((m) => [
              ...m,
              {
                id: crypto.randomUUID(),
                role: "assistant",
                content: exhaustedContent,
                createdAt: new Date().toISOString(),
                isError: true,
              },
            ]);
            setStatus("idle");
            busyRef.current = false;
            return;
          }
          setMessages((m) => m.filter((msg) => msg.id !== userMessageId));
          setStatus("idle");
          busyRef.current = false;
          setError(i18n.t("aiChat.errors.sendFailed"));
          return;
        }
      }

      const assistantId = crypto.randomUUID();
      let streamStarted = false;
      let streamRaf: number | null = null;

      try {
        if (!chatRef.current) {
          const summary = buildSummary(
            data.wedding,
            data.guests,
            data.tasks,
            data.budgetItems,
            data.activePlan?.name ?? null,
            data.tables.length,
          );
          const memory = await loadPlannerMemory(data.wedding.id).catch(() => null);
          const model = getPlannerModel(buildSystemInstruction(summary, skillIndex, memory));
          chatRef.current = model.startChat();
        }

        let userContent: string | Part[];
        if (validFiles.length > 0) {
          const parts: Part[] = [{ text: trimmed || " " }];
          for (const f of validFiles) {
            const data64 = await fileToBase64(f);
            parts.push({ inlineData: { mimeType: f.type, data: data64 } });
          }
          userContent = parts;
        } else {
          userContent = trimmed;
        }

        const ctx = makeToolContext(data.wedding.id);
        let streamedText = "";

        // Flush the accumulated stream into the live bubble. Coalesced via
        // requestAnimationFrame so token-sized chunks don't trigger a re-render
        // each (~one update per frame). The first flush swaps the typing
        // indicator for a growing assistant bubble.
        const flushStream = () => {
          streamRaf = null;
          const isFirst = !streamStarted;
          streamStarted = true;
          const text = streamedText;
          if (isFirst) setProgressLabel(null);
          setMessages((m) =>
            isFirst
              ? [
                  ...m,
                  {
                    id: assistantId,
                    role: "assistant",
                    content: text,
                    createdAt: new Date().toISOString(),
                    streaming: true,
                  },
                ]
              : m.map((msg) => (msg.id === assistantId ? { ...msg, content: text } : msg)),
          );
        };

        const result = await runAgentTurnStream(chatRef.current, userContent, ctx, {
          requestConfirmation,
          requestQuestionnaire,
          onProgress: setProgressLabel,
          onTextChunk: (chunk) => {
            streamedText += chunk;
            // Coalesce per frame; fall back to a sync flush where rAF is absent.
            if (typeof requestAnimationFrame !== "function") {
              flushStream();
            } else if (streamRaf === null) {
              streamRaf = requestAnimationFrame(flushStream);
            }
          },
        });

        // Cancel any pending flush so it cannot overwrite the finalised text.
        if (streamRaf !== null && typeof cancelAnimationFrame === "function") {
          cancelAnimationFrame(streamRaf);
          streamRaf = null;
        }

        // Finalise: trust result.text (matches the non-streaming reply exactly)
        // and attach the tool activities + grounding sources to the bubble.
        const finalText = result.text || streamedText;
        const activities = result.activities.length ? result.activities : undefined;
        const sources = result.sources.length ? result.sources : undefined;
        setMessages((m) =>
          streamStarted
            ? m.map((msg) =>
                msg.id === assistantId
                  ? { ...msg, content: finalText, streaming: false, activities, sources }
                  : msg,
              )
            : [
                ...m,
                {
                  id: assistantId,
                  role: "assistant",
                  content: finalText,
                  createdAt: new Date().toISOString(),
                  activities,
                  sources,
                },
              ],
        );
      } catch (err) {
        chatRef.current = null;
        console.error("[AI planner]", err);
        const language = data.wedding?.settings.language ?? "pl";
        // Drop any half-streamed bubble before surfacing the error.
        if (streamStarted) {
          setMessages((m) => m.filter((msg) => msg.id !== assistantId));
        }
        if (err instanceof AiNotConfiguredError || isConfigError(err)) {
          setNotConfigured(true);
          setError(formatPlannerError(err, language));
        } else {
          setMessages((m) => [
            ...m,
            {
              id: crypto.randomUUID(),
              role: "assistant",
              content: formatPlannerError(err, language),
              createdAt: new Date().toISOString(),
              isError: true,
            },
          ]);
        }
      } finally {
        if (streamRaf !== null && typeof cancelAnimationFrame === "function") {
          cancelAnimationFrame(streamRaf);
          streamRaf = null;
        }
        setStatus("idle");
        setProgressLabel(null);
        setPendingConfirmation(null);
        setPendingQuestionnaire(null);
        confirmResolverRef.current = null;
        questionnaireResolverRef.current = null;
        busyRef.current = false;
      }
    },
    [
      makeToolContext,
      requestConfirmation,
      requestQuestionnaire,
      skillIndex,
      firebaseUser,
      effectivePlan,
      isDemoUser,
    ],
  );

  const sendSync = useCallback(
    (text: string, files?: File[]) => {
      void send(text, files);
    },
    [send],
  );

  const reset = useCallback(() => {
    chatRef.current = null;
    confirmResolverRef.current = null;
    questionnaireResolverRef.current = null;
    busyRef.current = false;
    setMessages([buildGreeting(plannerLanguage)]);
    setStatus("idle");
    setProgressLabel(null);
    setPendingConfirmation(null);
    setPendingQuestionnaire(null);
    setError(null);
    pendingLanguageResetRef.current = false;
  }, [plannerLanguage]);

  useEffect(() => {
    const onLanguageChanged = () => {
      if (status !== "idle") {
        pendingLanguageResetRef.current = true;
        return;
      }
      reset();
    };
    i18n.on("languageChanged", onLanguageChanged);
    return () => i18n.off("languageChanged", onLanguageChanged);
  }, [status, reset]);

  useEffect(() => {
    if (status === "idle" && pendingLanguageResetRef.current) {
      pendingLanguageResetRef.current = false;
      reset();
    }
  }, [status, reset]);

  return {
    messages,
    status,
    progressLabel,
    pendingConfirmation,
    pendingQuestionnaire,
    notConfigured,
    error,
    ready: wedding !== null,
    send: sendSync,
    confirm,
    submitQuestionnaire,
    cancelQuestionnaire,
    reset,
  };
}
